import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { TrendingUp } from 'lucide-react';

import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Button from '../../components/ui/Button';
import { PageEmpty } from '../performance-operations/styles';
import { formatDate, formatDateTime } from '../../utils/Utils';
import HistoryImportDrawer from './HistoryImportDrawer';
import { useHistoryImportApi } from './config';

// PM analitik P6 — geçmiş dönem sonuçlarının Excel ile içe aktarılması (/performance/history-import; yalnız İK). Liste sayfası
// EmployeesPage aynası: başlık yalnız DataTable'da, "Yeni İçe Aktarma" tablonun içinde; oluşturma ve yeniden yükleme
// GenericFormDrawer kabuğunda (HistoryImportDrawer), kaldırma ConfirmDialog ile. Tek kaydırma: yalnız tablo satırları kayar.
const Container = styled.div`
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const TableArea = styled.div`
  flex: 1;
  min-height: 0;
`;

export default function PerformanceHistoryImportPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const history = useHistory();
  const [query, setQuery] = useState({ pageNumber: 1, pageSize: 20 });
  const { data, total, fetching, failed, refresh, remove } = useHistoryImportApi(query);
  const [drawer, setDrawer] = useState({ open: false, period: null });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [height, setHeight] = useState(0);
  const areaRef = useRef(null);

  useEffect(() => {
    const el = areaRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => entries.forEach((e) => setHeight(e.contentRect.height)));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const rows = useMemo(() => data.map((r) => ({
    ...r,
    id: r.periodId,
    dates: `${formatDate(r.startDate, lang)} – ${formatDate(r.endDate, lang)}`,
    kind: r.resultKind === 'level'
      ? `${t('performance.historyImport.kind.level')}${r.ratingScaleName ? ` · ${r.ratingScaleName}` : ''}`
      : t('performance.historyImport.kind.score'),
    participants: t('performance.historyImport.participantsCell', { total: r.participantCount, final: r.withFinalCount }),
    lastImport: r.lastImportedAt ? formatDateTime(r.lastImportedAt, lang) : '-',
    lastImportedBy: r.lastImportedBy || '-',
    lastFileName: r.lastFileName || '-',
  })), [data, t, lang]);

  const actions = useMemo(() => [
    { type: 'upload', label: t('performance.historyImport.action.reupload'), onClick: (row) => setDrawer({ open: true, period: row }) },
    {
      type: 'open', icon: TrendingUp, label: t('performance.historyImport.action.trends'),
      onClick: (row) => history.push(`/performance/trends?periods=${row.periodId}`),
    },
    { type: 'divider' },
    { type: 'delete', label: t('performance.historyImport.action.delete'), onClick: (row) => setDeleteTarget(row) },
  ], [t, history]);

  const columns = useMemo(() => [
    { name: 'name', label: t('performance.historyImport.col.name'), options: { filter: false, sort: false } },
    { name: 'dates', label: t('performance.historyImport.col.dates'), options: { filter: false, sort: false } },
    { name: 'kind', label: t('performance.historyImport.col.kind'), options: { filter: false, sort: false } },
    { name: 'participants', label: t('performance.historyImport.col.participants'), options: { filter: false, sort: false } },
    { name: 'lastImport', label: t('performance.historyImport.col.lastImport'), options: { filter: false, sort: false } },
    { name: 'lastImportedBy', label: t('performance.historyImport.col.lastImportedBy'), options: { filter: false, sort: false } },
    { name: 'lastFileName', label: t('performance.historyImport.col.lastFileName'), options: { filter: false, sort: false } },
    {
      name: 'actions', label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => <OptionsMenu actions={actions} row={rows[tableMeta.rowIndex]} />,
      },
    },
  ], [t, actions, rows]);

  const onTableChanged = useCallback((action, tableState) => {
    if (action === 'changePage' || action === 'changeRowsPerPage') {
      setQuery({ pageNumber: (tableState.page || 0) + 1, pageSize: tableState.rowsPerPage || 20 });
    }
  }, []);

  const onDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    await remove(deleteTarget.periodId);
    setDeleting(false);
    setDeleteTarget(null);
  };

  return (
    <Container>
      <TableArea ref={areaRef}>
        {failed && !fetching ? (
          <PageEmpty data-state="error">
            <div>{t('performance.operations.panel.error')}</div>
            <Button variant="outline" onClick={refresh} data-action="retry">{t('performance.operations.panel.retry')}</Button>
          </PageEmpty>
        ) : (
          <DataTable
            title={t('performance.historyImport.title')}
            columns={columns}
            data={rows}
            totalRowCount={total}
            pageSize={query.pageSize}
            pageIndex={query.pageNumber - 1}
            onTableChanged={onTableChanged}
            fetching={fetching}
            disableFilters
            canCreateNewRecord
            newButtonLabel={t('performance.historyImport.new')}
            onNewButtonClick={() => setDrawer({ open: true, period: null })}
            tableBodyMaxHeight={height ? `${Math.max(200, height - 130)}px` : undefined}
          />
        )}
      </TableArea>
      <HistoryImportDrawer
        open={drawer.open}
        period={drawer.period}
        onClose={() => setDrawer({ open: false, period: null })}
        onImported={refresh}
      />
      <ConfirmDialog
        isOpen={!!deleteTarget}
        title={t('performance.historyImport.deleteTitle')}
        message={t('performance.historyImport.deleteMessage', { name: deleteTarget?.name || '' })}
        confirmText={t('performance.historyImport.action.delete')}
        cancelText={t('common.cancel')}
        loading={deleting}
        onConfirm={onDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Container>
  );
}
